import { FeedbackState, type FeedbackVariant } from "@c/shared";
import { cn } from "@u/cn";
import { Skeleton } from "@ui/skeleton";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

// ── DashboardEmptyState ──────────────────────────────────────────────────────
// Thin semantic map over the canonical FeedbackState.

export type DashboardEmptyVariant =
  | "noData"
  | "noResults"
  | "onboarding"
  | "errorRecovery";

const EMPTY_MAP: Record<DashboardEmptyVariant, FeedbackVariant> = {
  noData: "empty",
  noResults: "no-results",
  onboarding: "onboarding",
  errorRecovery: "error",
};

export interface DashboardEmptyStateProps {
  variant?: DashboardEmptyVariant;
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  surface?: "dashed" | "plain" | "panel";
  className?: string;
}

export function DashboardEmptyState({
  variant = "noData",
  title,
  description,
  icon,
  action,
  secondaryAction,
  surface = "dashed",
  className,
}: DashboardEmptyStateProps) {
  return (
    <FeedbackState
      variant={EMPTY_MAP[variant]}
      title={title}
      description={description}
      icon={icon}
      primaryAction={action}
      secondaryAction={secondaryAction}
      surface={surface}
      className={className}
    />
  );
}

// ── DashboardAlert ───────────────────────────────────────────────────────────
// Inline banner (not a full-region empty state).

export type DashboardAlertVariant = "success" | "warning" | "error" | "info";

const ALERT_STYLE: Record<
  DashboardAlertVariant,
  { icon: LucideIcon; tone: string; role: "status" | "alert" }
> = {
  success: {
    icon: CheckCircle2,
    tone: "text-success-strong bg-success-subtle border-success/20",
    role: "status",
  },
  warning: {
    icon: TriangleAlert,
    tone: "text-warning-strong bg-warning-subtle border-warning/20",
    role: "status",
  },
  error: {
    icon: AlertCircle,
    tone: "text-destructive-strong bg-destructive-subtle border-destructive/20",
    role: "alert",
  },
  info: {
    icon: Info,
    tone: "text-primary bg-primary/10 border-primary/20",
    role: "status",
  },
};

export interface DashboardAlertProps {
  variant: DashboardAlertVariant;
  title?: string;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
  children?: ReactNode;
}

export function DashboardAlert({
  variant,
  title,
  action,
  onDismiss,
  className,
  children,
}: DashboardAlertProps) {
  const { t } = useTranslation();
  const s = ALERT_STYLE[variant];
  const Icon = s.icon;
  return (
    <div
      role={s.role}
      className={cn(
        "flex items-start gap-3 rounded-xl border px-4 py-3 text-sm",
        s.tone,
        className,
      )}
    >
      <Icon size={16} className="shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && (
          <div className={cn(title && "mt-0.5 opacity-90")}>{children}</div>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("dismiss")}
          className="shrink-0 opacity-70 hover:opacity-100 transition-opacity"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

// ── DashboardLoading ─────────────────────────────────────────────────────────

export type DashboardLoadingVariant = "page" | "table" | "list" | "card";

export interface DashboardLoadingProps {
  variant?: DashboardLoadingVariant;
  rows?: number;
  className?: string;
}

export function DashboardLoading({
  variant = "list",
  rows = 4,
  className,
}: DashboardLoadingProps) {
  const items = Array.from({ length: rows }, (_, i) => i);

  if (variant === "card") {
    return (
      <div className={cn("grid grid-cols-1 sm:grid-cols-3 gap-4", className)}>
        {items.map((i) => (
          <Skeleton key={i} className="h-32 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (variant === "page") {
    return (
      <div className={cn("space-y-6", className)} aria-busy="true">
        <Skeleton className="h-10 w-64 rounded-xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  // table | list
  return (
    <div className={cn("space-y-3", className)} aria-busy="true">
      {items.map((i) => (
        <Skeleton key={i} className="h-12 w-full rounded-xl" />
      ))}
    </div>
  );
}

// ── Page-level states ────────────────────────────────────────────────────────

export function DashboardPageLoading({ className }: { className?: string }) {
  return <DashboardLoading variant="page" className={className} />;
}

export interface DashboardPageStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
}

export function DashboardPageEmpty({
  title,
  description,
  action,
}: DashboardPageStateProps) {
  return (
    <DashboardEmptyState
      variant="noData"
      title={title}
      description={description}
      action={action}
    />
  );
}

export function DashboardPageError({
  title,
  description,
  action,
}: DashboardPageStateProps) {
  return (
    <FeedbackState
      variant="error"
      surface="dashed"
      title={title}
      description={description}
      primaryAction={action}
    />
  );
}
